import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios, { AxiosError } from "axios";
import Input from "../../components/form/input/InputField";
import { DatePicker } from "../../components/common/DatePicker";
import ListboxComponent from "../../components/common/ListboxComponent";
import { styles } from "../../styles/pages/Opportunity/OpportunityEditStyles";
import { Modal } from "../../components/ui/modal";
import { CheckCircle } from "lucide-react";
import Loader from "../../components/common/Loader";

// Type definitions
interface Stage {
  id: number;
  stage_name: string;
}
interface Source {
  id: number;
  source_name: string;
}
// interface User {
//   id: number;
//   name: string;
// }
interface Lead {
  id: number;
  name: string;
}
interface Status {
  id: number;
  status_name: string;
}

interface FormData {
  opportunitie_name: string;
  opp_amount: string;
  probability: string;
  stage_id: string;
  source_id: string;
  // user_id: string;
  status_id: string;
  lead_id: string;
  expected_close_date: string;
  description: string;
}

interface FormErrors {
  opportunitie_name?: string;
  opp_amount?: string;
  probability?: string;
  stage_id?: string;
  source_id?: string;
  // user_id?: string;
  status_id?: string;
  lead_id?: string;
  expected_close_date?: string;
}

const OpportunityEdit = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    opportunitie_name: "",
    opp_amount: "",
    probability: "",
    stage_id: "0",
    source_id: "0",
    // user_id: "0",
    status_id: "0",
    lead_id: "0",
    expected_close_date: "",
    description: "",
  });
  const [stages, setStages] = useState<Stage[]>([]);
  const [sources, setSources] = useState<Source[]>([]);
  // const [users, setUsers] = useState<User[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(true);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [dateOfEvent, setDateOfEvent] = useState<string>("");
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

  // Fetch opportunity data on mount
  useEffect(() => {
    if (!id) {
      setSuccessModalOpen(false);
      navigate("/opportunity-list");
      return;
    }

    const fetchOpportunitieData = async () => {
      setLoading(true);

      const token = sessionStorage.getItem("access_token");
      if (!token) {
        console.warn("No access token found, redirecting to /signin");
        setSuccessModalOpen(false);
        navigate("/signin");
        return;
      }

      try {
        const response = await axios.post(
          `${API_URL}/api/opportunitie/edit`,
          { opportunitie_id: id },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.data.status !== 200) {
          throw new Error(
            response.data.message || "Failed to fetch opportunity data"
          );
        }

        const data = response.data.data[0];
        if (!data) {
          throw new Error("No opportunity data found for the given ID");
        }

        // Ensure the date is in DD-MM-YYYY format
        const expectedCloseDate = data.expected_close_date
          ? data.expected_close_date
          : "";

        setFormData({
          opportunitie_name: data.opportunitie_name || "",
          opp_amount: data.opp_amount || "",
          probability: data.probability || "",
          stage_id: String(data.stage_id) || "0",
          source_id: String(data.source_id) || "0",
          // user_id: String(data.user_id) || "0",
          status_id: String(data.status_id) || "0",
          lead_id: String(data.lead_id) || "0",
          expected_close_date: expectedCloseDate,
          description: data.description || "",
        });

        setDateOfEvent(expectedCloseDate);

        // Validate if the fetched expected_close_date is empty
        if (!expectedCloseDate) {
          setErrors((prev) => ({
            ...prev,
            expected_close_date: "The Expected close date field is required",
          }));
        }
      } catch (error) {
        const err = error as AxiosError<{ message?: string }>;
        console.error("Fetch Error Details:", {
          message: err.message,
          response: err.response?.data,
          status: err.response?.status,
          request: {
            url: err.config?.url,
            data: err.config?.data,
            headers: err.config?.headers,
          },
        });
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunitieData();
  }, [id, navigate, API_URL]);

  // Fetch dropdown data (stages, sources, users, leads, statuses)
  useEffect(() => {
    const token = sessionStorage.getItem("access_token");
    if (!token) return;

    const fetchDropdownData = async () => {
      try {
        const [stagesRes, sourcesRes, leadsRes, statusesRes] =
          await Promise.all([
            fetch(`${API_URL}/api/stage/name/list`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_URL}/api/source/name/list`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            // fetch(`${API_URL}/api/user/name/list`, {
            //   headers: { Authorization: `Bearer ${token}` },
            // }),
            fetch(`${API_URL}/api/account/name/list`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            fetch(`${API_URL}/api/status/name/list`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

        if (stagesRes.status === 200) setStages((await stagesRes.json()).data);
        if (sourcesRes.status === 200)
          setSources((await sourcesRes.json()).data);
        // if (usersRes.status === 200) setUsers((await usersRes.json()).data);
        if (leadsRes.status === 200) setLeads((await leadsRes.json()).data);
        if (statusesRes.status === 200)
          setStatuses((await statusesRes.json()).data);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };

    fetchDropdownData();
  }, [API_URL]);

  // Input change handler with real-time validation
  const handleInputChange =
    (field: keyof FormData) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        setFormData((prev) => ({ ...prev, [field]: value }));

        if (!value.trim()) {
          setErrors((prev) => ({
            ...prev,
            [field]:
              field === "opportunitie_name"
                ? "The Opportunity name field is required"
                : field === "opp_amount"
                  ? "The Amount field is required"
                  : field === "probability"
                    ? "The Probability field is required"
                    : undefined,
          }));
        } else {
          setErrors((prev) => ({
            ...prev,
            [field]:
              field === "opp_amount" && (isNaN(Number(value)) || Number(value) <= 0)
                ? "Amount must be a positive number"
                : field === "opp_amount"
                  ? undefined
                  : field === "probability" &&
                    (isNaN(Number(value)) || Number(value) < 0 || Number(value) > 100)
                    ? "Probability must be between 0 and 100"
                    : field === "probability"
                      ? undefined
                      : undefined,
          }));
        }
      };

  // Dropdown change handler
  const handleDropdownChange = (field: keyof FormData) => (item: any) => {
    const value = item ? String(item.id) : "0";
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({
      ...prev,
      [field]:
        value === "0"
          ? `The ${field.replace("_id", "")} field is required`
          : undefined,
    }));
  };

  // Date change handler with validation
  const handleDateChange = (selectedDates: Date[]) => {
    const date = selectedDates[0];
    const formattedDate = date
      ? `${date.getDate().toString().padStart(2, "0")}-${(date.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${date.getFullYear()}`
      : "";
    setDateOfEvent(formattedDate);
    setFormData((prev) => ({ ...prev, expected_close_date: formattedDate }));
    setErrors((prev) => ({
      ...prev,
      expected_close_date: !formattedDate
        ? "The Expected close date field is required"
        : undefined,
    }));
  };

  // Form submission handler with validation
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});

    const {
      opportunitie_name,
      opp_amount,
      probability,
      stage_id,
      source_id,
      // user_id,
      status_id,
      lead_id,
      expected_close_date,
      description,
    } = formData;
    const newErrors: FormErrors = {};

    if (!opportunitie_name)
      newErrors.opportunitie_name = "The Opportunity name field is required";
    if (!opp_amount)
      newErrors.opp_amount = "The Amount field is required";
    else if (isNaN(Number(opp_amount)) || Number(opp_amount) <= 0)
      newErrors.opp_amount = "Amount must be a positive number";
    if (!probability)
      newErrors.probability = "The Probability field is required";
    else if (
      isNaN(Number(probability)) ||
      Number(probability) < 0 ||
      Number(probability) > 100
    )
      newErrors.probability = "Probability must be between 0 and 100";
    if (stage_id === "0") newErrors.stage_id = "The Stage field is required";
    if (source_id === "0") newErrors.source_id = "The Source field is required";
    // if (user_id === "0")
    //   newErrors.user_id = "The Assigned to field is required";
    if (status_id === "0") newErrors.status_id = "The Status field is required";
    if (lead_id === "0") newErrors.lead_id = "The Lead field is required";
    if (!expected_close_date)
      newErrors.expected_close_date = "The Expected close date field is required";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setSaving(false);
      return;
    }

    const token = sessionStorage.getItem("access_token");

    if (!token || !id) {
      setSaving(false);
      navigate("/signin");
      return;
    }

    const submitData = new FormData();
    submitData.append("opportunitie_id", id);
    submitData.append("opportunitie_name", opportunitie_name);
    submitData.append("expected_close_date", expected_close_date);
    submitData.append("opp_amount", opp_amount);
    submitData.append("probability", probability);
    submitData.append("description", description);
    submitData.append("lead_id", lead_id);
    // submitData.append("user_id", user_id);
    submitData.append("status_id", status_id);
    submitData.append("stage_id", stage_id);
    submitData.append("source_id", source_id);

    try {
      const response = await axios.post(
        `${API_URL}/api/opportunitie/update`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.status === 200) {
        setSuccessModalOpen(true);
        setTimeout(() => {
          setSuccessModalOpen(false);
          navigate("/opportunity-list");
        }, 2000);
      } else {
        throw new Error(
          response.data.message || `Server returned status: ${response.data.status}`
        );
      }
    } catch (error) {
      const err = error as AxiosError<{
        message?: string;
        errors?: Record<string, string[]>;
      }>;
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        "An error occurred while updating the opportunity";

      if (err.response?.status === 422 && err.response.data?.errors) {
        const serverErrors = Object.fromEntries(
          Object.entries(err.response.data.errors).map(([key, value]) => [
            key,
            value[0],
          ])
        );
        setErrors(serverErrors);
      } else if (err.response?.status === 401) {
        navigate("/signin");
      } else {
        console.error(errorMessage);
        setErrors({ opportunitie_name: errorMessage });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Edit Opportunity</h2>
      </div>
      {loading && <Loader />}
      {!loading && (
        <div className={styles.formContainer}>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.opportunitieDetails}>
              <ListboxComponent
                value={
                  leads?.find((lead) => lead.id === Number(formData.lead_id)) ?? null
                }
                onChange={handleDropdownChange("lead_id")}
                items={leads || []}  // <- Defensive fallback
                label="Lead"
                error={errors.lead_id}
                displayKey="name"
                required
              />



              <div>
                <label className={styles.label}>
                  Opportunity Name <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.opportunitie_name}
                  onChange={handleInputChange("opportunitie_name")}
                  className={`${styles.inputField} ${errors.opportunitie_name
                      ? styles.inputError
                      : styles.inputDefault
                    }`}
                />
                {errors.opportunitie_name && (
                  <span className={styles.errorText}>
                    {errors.opportunitie_name}
                  </span>
                )}
              </div>

              <div>
                <label className={styles.label}>
                  Amount <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.opp_amount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setFormData((prev) => ({ ...prev, opp_amount: value }));
                    if (!value.trim()) {
                      setErrors((prev) => ({
                        ...prev,
                        opp_amount: "The Amount field is required",
                      }));
                    } else {
                      setErrors((prev) => ({
                        ...prev,
                        opp_amount:
                          isNaN(Number(value)) || Number(value) <= 0
                            ? "Amount must be a positive number"
                            : undefined,
                      }));
                    }
                  }}
                  className={`${styles.inputField} ${errors.opp_amount ? styles.inputError : styles.inputDefault
                    }`}
                />
                {errors.opp_amount && (
                  <span className={styles.errorText}>{errors.opp_amount}</span>
                )}
              </div>

              <ListboxComponent
                value={
                  stages.find(
                    (stage) => stage.id === Number(formData.stage_id)
                  ) ?? null
                }
                onChange={handleDropdownChange("stage_id")}
                items={stages}
                label="Stage"
                error={errors.stage_id}
                displayKey="stage_name"
                required
              />

              {/* <ListboxComponent
                value={
                  users.find((user) => user.id === Number(formData.user_id)) ??
                  null
                }
                onChange={handleDropdownChange("user_id")}
                items={users}
                error={errors.user_id}
                label="Assigned To"
                displayKey="name"
                required
              /> */}

              <div>
                <DatePicker
                  value={dateOfEvent}
                  onChange={handleDateChange}
                  label="Expected Close Date"
                  error={errors.expected_close_date}
                  required
                />
              </div>

              <div>
                <label className={styles.label}>
                  Probability <span className={styles.required}>*</span>
                </label>
                <Input
                  type="text"
                  value={formData.probability}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setFormData((prev) => ({ ...prev, probability: value }));
                    if (!value.trim()) {
                      setErrors((prev) => ({
                        ...prev,
                        probability: "The Probability field is required",
                      }));
                    } else {
                      setErrors((prev) => ({
                        ...prev,
                        probability:
                          isNaN(Number(value)) || Number(value) < 0 || Number(value) > 100
                            ? "Probability must be between 0 and 100"
                            : undefined,
                      }));
                    }
                  }}
                  className={`${styles.inputField} ${errors.probability ? styles.inputError : styles.inputDefault
                    }`}
                />
                {errors.probability && (
                  <span className={styles.errorText}>{errors.probability}</span>
                )}
              </div>

              <ListboxComponent
                value={
                  sources.find(
                    (source) => source.id === Number(formData.source_id)
                  ) ?? null
                }
                onChange={handleDropdownChange("source_id")}
                items={sources}
                label="Source"
                error={errors.source_id}
                displayKey="source_name"
                required
              />

              <ListboxComponent
                value={
                  statuses.find(
                    (status) => status.id === Number(formData.status_id)
                  ) ?? null
                }
                onChange={handleDropdownChange("status_id")}
                items={statuses}
                label="Status"
                error={errors.status_id}
                displayKey="status_name"
                required
              />

              <div>
                <label className={styles.label}>Description</label>
                <textarea
                  rows={6}
                  value={formData.description}
                  onChange={handleInputChange("description")}
                  className={`${styles.textareaField}`}
                  style={{ textAlign: "justify" }}
                />
              </div>
            </div>

            <div className={styles.buttonsContainer}>
              <button
                className={styles.submitButton}
                type="submit"
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                className={styles.cancelButton}
                type="button"
                onClick={() => navigate("/opportunity-list")}
              >
                Cancel
              </button>
            </div>

            <Modal
              isOpen={isSuccessModalOpen}
              onClose={() => setSuccessModalOpen(false)}
              className={styles.modal}
            >
              <button
                onClick={() => setSuccessModalOpen(false)}
                className={styles.modalCloseButton}
              ></button>
              <div className="flex justify-center">
                <div className={styles.modalIconContainer}>
                  <CheckCircle className="w-6 h-6" />
                </div>
              </div>
              <h4 className={styles.modalTitle}>
                Opportunity Updated Successfully
              </h4>
              <p className={styles.modalMessage}>
                The opportunity has been updated in the system successfully.
              </p>
              <div className={styles.modalButtonContainer}>
                <button
                  onClick={() => setSuccessModalOpen(false)}
                  className={styles.modalButton}
                >
                  OK
                </button>
              </div>
            </Modal>
          </form>
        </div>
      )}
    </div>
  );
};

export default OpportunityEdit;